import {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import MiniSearch from 'minisearch';
import { MatterData, MatterDocument } from '@/database/baseTypes';
import { useMatters } from '@/database/hooks/matters';
import { WhereClause } from '@/database/queryBuilder';
import { MatterOption } from '@/components/AltMatterAutocomplete/types';

interface MatterSearchContextValue {
  loading: boolean;
  error: Error | undefined;
  matters: MatterDocument[];
  search: (query: string, where?: WhereClause<MatterData>[]) => MatterOption[];
  getMatterById: (id: string) => MatterDocument | undefined;
  mattersById: Record<string, MatterDocument>;
  ensureMatterSearchStarted: () => void;
}

const EMPTY_MATTERS: MatterDocument[] = [];
const ensureMockMatterSearchStarted = () => undefined;

const MatterSearchContext = createContext<MatterSearchContextValue | null>(
  null,
);

const createMiniSearchInstance = () => {
  return new MiniSearch<MatterDocument>({
    fields: ['name', 'scope', 'description'],
    searchOptions: {
      boost: { name: 10 },
      fuzzy: 0.3,
      prefix: true,
    },
  });
};

export const MatterSearchProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const [hasMatterSearchStarted, setHasMatterSearchStarted] = useState(() =>
    isMatterPath(pathname),
  );
  const ensureMatterSearchStarted = useCallback(
    () => setHasMatterSearchStarted(true),
    [],
  );
  const mattersLoadState = useMatters({ disabled: !hasMatterSearchStarted });
  const matters =
    mattersLoadState.status === 'loaded'
      ? mattersLoadState.data
      : EMPTY_MATTERS;
  const error =
    mattersLoadState.status === 'error' ? mattersLoadState.error : undefined;

  useEffect(() => {
    if (isMatterPath(pathname)) ensureMatterSearchStarted();
  }, [ensureMatterSearchStarted, pathname]);

  // Note: Do not use `useMemo` here. React does not guarantee that the memoized
  // value will be preserved between renders.
  const [miniSearch] = useState(() => createMiniSearchInstance());

  const mattersById = useMemo(() => {
    if (mattersLoadState.status !== 'loaded') {
      return {};
    }
    return mattersLoadState.data.reduce(
      (acc, matter) => {
        acc[matter.id] = matter;
        return acc;
      },
      {} as Record<string, MatterDocument>,
    );
  }, [mattersLoadState]);

  const baseMatterOptions = useMemo(
    () => makeMatterOptions(matters),
    [matters],
  );

  // Update the MiniSearch index when matters change
  useEffect(() => {
    if (mattersLoadState.status !== 'loaded') {
      return;
    }
    miniSearch.removeAll();
    miniSearch.addAll(mattersLoadState.data);
  }, [mattersLoadState, miniSearch]);

  const search = useMemo(() => {
    return (
      query: string,
      where?: WhereClause<MatterData>[],
    ): MatterOption[] => {
      const searchText = (query ?? '').toLowerCase().trim();
      const results =
        searchText.length === 0
          ? baseMatterOptions
          : hydrateSearchResults(miniSearch, searchText, mattersById);

      // Apply client-side filtering if where clause provided
      if (where && where.length > 0) {
        return results.filter((option) =>
          where.every((clause) => matchesWhereClause(option.matter, clause)),
        );
      }

      return results;
    };
  }, [baseMatterOptions, mattersById, miniSearch]);

  const getMatterById = useMemo(() => {
    return (id: string): MatterDocument | undefined => mattersById[id];
  }, [mattersById]);

  const contextValue = useMemo(
    (): MatterSearchContextValue => ({
      loading: mattersLoadState.status === 'loading',
      error,
      matters,
      search,
      getMatterById,
      mattersById,
      ensureMatterSearchStarted,
    }),
    [
      mattersLoadState.status,
      error,
      matters,
      search,
      getMatterById,
      mattersById,
      ensureMatterSearchStarted,
    ],
  );

  return (
    <MatterSearchContext.Provider value={contextValue}>
      {children}
    </MatterSearchContext.Provider>
  );
};

export const useMatterSearch = (): MatterSearchContextValue => {
  const context = useContext(MatterSearchContext);
  if (context === null) {
    throw new Error(
      'useMatterSearch must be used within a MatterSearchProvider',
    );
  }
  return context;
};

/**
 * A mock provider for use in Storybook and tests.
 * Accepts static matter data instead of fetching from the database.
 */
export const MockMatterSearchProvider = ({
  children,
  matters,
}: {
  children: ReactNode;
  matters: MatterDocument[];
}) => {
  const [miniSearch] = useState(() => {
    const instance = createMiniSearchInstance();
    instance.addAll(matters);
    return instance;
  });

  const mattersById = useMemo(() => {
    return matters.reduce(
      (acc, matter) => {
        acc[matter.id] = matter;
        return acc;
      },
      {} as Record<string, MatterDocument>,
    );
  }, [matters]);

  const baseMatterOptions = useMemo(
    () => makeMatterOptions(matters),
    [matters],
  );

  const search = useMemo(() => {
    return (
      query: string,
      where?: WhereClause<MatterData>[],
    ): MatterOption[] => {
      const searchText = (query ?? '').toLowerCase().trim();
      const results =
        searchText.length === 0
          ? baseMatterOptions
          : hydrateSearchResults(miniSearch, searchText, mattersById);

      if (where && where.length > 0) {
        return results.filter((option) =>
          where.every((clause) => matchesWhereClause(option.matter, clause)),
        );
      }

      return results;
    };
  }, [baseMatterOptions, mattersById, miniSearch]);

  const getMatterById = useMemo(() => {
    return (id: string): MatterDocument | undefined => mattersById[id];
  }, [mattersById]);

  useEffect(() => {
    miniSearch.removeAll();
    miniSearch.addAll(matters);
  }, [matters, miniSearch]);

  const contextValue = useMemo(
    (): MatterSearchContextValue => ({
      loading: false,
      error: undefined,
      matters,
      search,
      getMatterById,
      mattersById,
      ensureMatterSearchStarted: ensureMockMatterSearchStarted,
    }),
    [matters, search, getMatterById, mattersById],
  );

  return (
    <MatterSearchContext.Provider value={contextValue}>
      {children}
    </MatterSearchContext.Provider>
  );
};

const makeMatterOptions = (matters: MatterDocument[]): MatterOption[] =>
  matters.map((matter) => ({
    value: matter.id,
    label: matter.name,
    matter,
    searchScore: 1,
  }));

const isMatterPath = (pathname: string) =>
  pathname === '/matters' ||
  pathname.startsWith('/matters/') ||
  pathname.startsWith('/matter/');

const hydrateSearchResults = (
  miniSearch: MiniSearch<MatterDocument>,
  searchText: string,
  mattersById: Record<string, MatterDocument>,
): MatterOption[] => {
  const options: MatterOption[] = [];
  for (const result of miniSearch.search(searchText)) {
    const matter = mattersById[result.id];
    if (matter !== undefined) {
      options.push({
        value: matter.id,
        label: matter.name,
        matter,
        searchScore: result.score,
      });
    }
  }
  return options;
};

// Helper to evaluate a single where clause against a matter
const matchesWhereClause = (
  matter: MatterDocument,
  clause: WhereClause<MatterData>,
): boolean => {
  const [field, operator, value] = clause;
  const matterValue = matter[field as keyof MatterDocument];

  switch (operator) {
    case '==':
      return matterValue === value;
    case '!=':
      return matterValue !== value;
    case '<':
      return matterValue !== undefined && matterValue < value;
    case '<=':
      return matterValue !== undefined && matterValue <= value;
    case '>':
      return matterValue !== undefined && matterValue > value;
    case '>=':
      return matterValue !== undefined && matterValue >= value;
    case 'in':
      return (
        matterValue !== undefined &&
        Array.isArray(value) &&
        value.includes(matterValue)
      );
    case 'not-in':
      return (
        matterValue !== undefined &&
        Array.isArray(value) &&
        !value.includes(matterValue)
      );
    case 'array-contains':
      return (
        matterValue !== undefined &&
        Array.isArray(matterValue) &&
        matterValue.includes(value)
      );
    case 'array-contains-any':
      return (
        matterValue !== undefined &&
        Array.isArray(matterValue) &&
        Array.isArray(value) &&
        value.some((v) => matterValue.includes(v))
      );
    default:
      return true;
  }
};
